import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WarningOctagon, WarningCircle, Package, ArrowUp, ImageSquare } from "@phosphor-icons/react";

export default function LowStock() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inventory/low-stock");
            setItems(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const outItems = items.filter((i) => i.stock_status === "out");
    const lowItems = items.filter((i) => i.stock_status === "low");

    return (
        <div className="space-y-6" data-testid="low-stock-page">
            <div>
                <div className="eyebrow mb-2">Alerts</div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Low & Out of Stock</h1>
                <p className="text-sm text-neutral-500 mt-1.5">
                    {outItems.length} out of stock · {lowItems.length} running low. Refill to restore inventory.
                </p>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500">Loading...</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-md py-16 text-center bg-white" data-testid="low-stock-empty">
                    <Package size={40} weight="thin" className="mx-auto text-neutral-300" />
                    <div className="mt-3 text-lg font-bold font-display">All stocked up</div>
                    <div className="text-sm text-neutral-500 mt-1">No products below minimum threshold. Great work!</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((p) => (
                        <LowStockRow key={p.id} product={p} onRefill={() => navigate(`/inventory?product=${p.id}&type=in`)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function LowStockRow({ product, onRefill }) {
    const img = product.images?.[0];
    const isOut = product.stock_status === "out";
    const bg = isOut ? "#FEE2E2" : "#FEF3C7";
    const fg = isOut ? "#DC2626" : "#B45309";
    const Icon = isOut ? WarningOctagon : WarningCircle;
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-4 flex items-center gap-4 fade-up hover:border-neutral-400 transition-colors"
             data-testid={`lowstock-row-${product.sku}`}>
            <div className="w-14 h-14 rounded overflow-hidden bg-neutral-100 shrink-0 flex items-center justify-center">
                {img ? <img src={fileUrl(img)} alt={product.name} className="w-full h-full object-cover" /> :
                    <ImageSquare size={22} weight="thin" className="text-neutral-300" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
                          style={{ background: bg, color: fg }}>
                        <Icon size={11} weight="bold" /> {isOut ? "Out of Stock" : "Low Stock"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-500">{product.sku}</span>
                </div>
                <div className="font-semibold text-neutral-900 mt-1 line-clamp-1">{product.name}</div>
                <div className="text-xs text-neutral-500 mt-1 tabular">
                    Current: <b className="text-neutral-900">{product.current_stock}</b> · Min: <b>{product.min_stock}</b> · Max: <b>{product.max_stock}</b>
                </div>
            </div>
            <Button onClick={onRefill} className="text-white shrink-0" style={{ backgroundColor: "#15803D" }} data-testid={`lowstock-refill-${product.sku}`}>
                <ArrowUp size={14} weight="bold" className="mr-1" /> Refill
            </Button>
        </div>
    );
}
